import { useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Link, Route, NavLink, Routes, useParams, useLocation } from 'react-router-dom';

// Third-party packages.
import Helmet from 'react-helmet';

// Helpers.
import Helper from "../../helpers/Helper";
import { getPlanAndTextTabName, getStatementTypeFromURL } from "../instances/tabs/utils";
import { getFilterParameters } from "../../helpers/utils";

// Contexts.
import { TimeRangeContext } from "../../context/TimeRangeContext";
import { FilterContext } from "../../context/FilterContext";

// Types.
import InstanceTarget from '../../types/instance/InstanceTarget';
import EventType from '../../types/instance/EventType';
import ExecutionPlan from '../../types/instance/ExecutionPlan';
import ChartPlotLine from "../../types/ChartPlotLine";

// Components.
import PeriodBreadcrumb from '../../component/PeriodBreadcrumb';
import Breadcrumb from "../../container/breadcrumb";
import HeaderActions from "../../container/header_actions";
import ConditionalRender from "../../helpers/ConditionalRender";
import FilterBar from "../../component/FilterBar";
import { OverflowTabbedMenu } from "../../container/overflow-tabbed-menu";
import TextAndPlan from "./tabs/TextAndPlan";
import StatementActivity from "./tabs/Activity";
import SqlStatistics from "./tabs/SQLStatistics";
import Batches from "../../component/instance/Tabs/Batches";
import GroupedBatches from "../../component/instance/Tabs/GroupedBatches";
import Events from "./tabs/Events";
import StatementDetailsPanel from "../instances/tabs/StatementDetailsPanel";

import { INSTANCE_TYPE_SQLSERVER } from "../../component/Constants";

interface IStatementDetailProps {
    instances: InstanceTarget[],
    eventTypes: EventType[],
    toggleMenu: Function,
    setFilterOptions: Function
}

const Statement = (props: IStatementDetailProps) => {
    const { instances } = props;
    const { instanceId, batchId, statementId, batchStatementId } = useParams();
    const getInstance: InstanceTarget[] = instances.filter(instance => instance.id === Number(instanceId))
    const instance = getInstance[0]
    const [chartPlanPlotLines, setChartPlanPlotLines] = useState<ChartPlotLine[]>([]);
    const location = useLocation();
    const timeRangeContext = useContext(TimeRangeContext);
    const { currentFilterOptions } = useContext(FilterContext);
    const [layout, setLayout] = useState<'vertical' | 'horizontal'>('vertical');

    const filterParameters = getFilterParameters(currentFilterOptions);

    // State for statement details side panel
    const [selectedStatement, setSelectedStatement] = useState<string | null>(null);
    const [selectedStatementType, setSelectedStatementType] = useState<'statement' | 'groupedstatement' | 'batch' | 'groupedbatch'>('statement');
    const [panelOpen, setPanelOpen] = useState(false);

    const openPanel = (statementId: string, statementType: 'statement' | 'groupedstatement' | 'batch' | 'groupedbatch' = 'statement') => {
        setSelectedStatement(statementId);
        setSelectedStatementType(statementType);
        setPanelOpen(true);
    };

    const closePanel = () => {
        setPanelOpen(false);
        setSelectedStatement(null);
    };

    const setExecutionPlanOptions = (executionPlans: ExecutionPlan[]) => {
        const chartCategories = Helper.getChartAxisCategories(timeRangeContext.timeRange.from, timeRangeContext.timeRange.to, timeRangeContext.timeRange.interval);
        setChartPlanPlotLines(Helper.getChartPlotLinesForExecutionPlans(timeRangeContext.timeRange, executionPlans, chartCategories))

    }

    const statementType = getStatementTypeFromURL()

    const shouldShowTab = (_value: string): boolean => {
        if (statementType.isBatch) {
            return false
        }
        return true
    }

    const getURL = (newSegment: string) => {
        const segments = location.pathname.split('/');
        segments[segments.length - 1] = newSegment;
        return segments.join('/');
    };

    const navItems = useMemo(() => [
        {
            label: getPlanAndTextTabName(statementType.type, instance).plans,
            icon: 'fal fa-file-code',
            path: getURL('text'),
            show: true,
            end: false,
        },
        {
            label: getPlanAndTextTabName(statementType.type, instance).activity,
            icon: 'fal fa-chart-pie-alt',
            path: getURL('activity'),
            show: true,
            end: false,
        },
        {
            label: !statementType.isGroup ? 'SQL Statistics' : 'Grouped SQL Statistics',
            icon: 'fal fa-chart-bar',
            path: getURL('sql-statistics'),
            show: shouldShowTab('sqlstatistics'),
            end: false,
        },
        {
            label: 'Batches',
            icon: 'fal fa-layer-group',
            path: getURL('batches'),
            show: instance?.type === INSTANCE_TYPE_SQLSERVER && !batchStatementId && statementType.isGroup && statementType.isBatch,
            end: false,
        },
        {
            label: !statementType.isGroup ? 'Calling Batches' : 'Calling Grouped Batches',
            icon: 'fal fa-layer-group',
            path: getURL('batches'),
            show: instance?.type === INSTANCE_TYPE_SQLSERVER && !batchStatementId && (statementType.type === 'statement' || statementType.type === 'groupedstatement'),
            end: false,
        },
        {
            label: 'Events',
            icon: 'fal fa-exchange',
            path: getURL('events'),
            show: true,
            end: false,
        },
    ], [instance, statementType, batchStatementId, getURL, shouldShowTab]);

    if (!instance) {
        return <></>;
    }

    return (
        <div id="content">
            <Helmet>
                <title>{instance.name} - Statement View - DBmarlin</title>
                <meta name="description"
                    content="Instance statement performance view, showing key performance metrics and detailed executed statement and waits information" />
            </Helmet>

            {/* Heading */}
            <div id="top-header-wrapper" className="row row-cols-lg-3 row-cols-sm-1 row-cols-md-2">
                <Breadcrumb instanceId={instance.id}
                    type="instances"
                    heading={instance.name}>
                    <Link to="/instances">Analysis</Link>
                    <Link to="/instances">Instances</Link>
                    <Link to={`/instances/${instanceId}/activity`}>{instance.name}</Link>
                    <Link
                        to={`/instances/${instanceId}/${statementType.isGroup ? 'activity/groupedstatements' : 'activity'}`}>{statementType.isGroup ? 'Grouped Activity' : 'Activity'}</Link>

                    <ConditionalRender if={batchStatementId}>
                        <Link
                            to={`/instances/${instanceId}/activity/${statementType.isGroup ? 'groupedbatch' : 'batch'}/${batchId}/activity`}>{statementType.isGroup ? 'Gr Batch' : 'Batch '} {((batchId && batchId.length > 50) ? `${batchId?.substring(0, 50)}...` : batchId)}</Link>
                        <NavLink to={`/instances/`}
                            className="current-breadcrumb-link pe-none">{statementType.isGroup ? 'Gr Statement ' : 'Statement '}{batchStatementId}</NavLink>
                    </ConditionalRender>

                    <ConditionalRender if={!batchStatementId}>
                        <NavLink to={`/instances/`}
                            className="current-breadcrumb-link pe-none">{statementType.breadcrumb} {statementId || batchId}</NavLink>
                    </ConditionalRender>
                    <PeriodBreadcrumb />
                </Breadcrumb>

                <HeaderActions toggleMenu={props.toggleMenu} refresh={() => {
                }}
                               />
            </div>


            <div className="loader" />
            <OverflowTabbedMenu
                items={navItems}
                rightContent={
                    location.pathname.endsWith('/text') ? (
                        <button
                            onClick={() => setLayout(l => l === 'vertical' ? 'horizontal' : 'vertical')}
                            title={layout === 'vertical' ? 'Switch to left/right split' : 'Switch to top/bottom split'}
                            style={{
                                background: 'none',
                                border: '1px solid #dee2e6',
                                borderRadius: '4px',
                                padding: '0.2rem 0.45rem',
                                cursor: 'pointer',
                                color: '#6c757d',
                                lineHeight: 1,
                            }}
                        >
                            <i
                                className="fal fa-table-columns fa-fw"
                                style={{ transform: layout === 'vertical' ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }}
                                aria-hidden="true"
                            />
                        </button>
                    ) : null
                }
            />

            {/* Filters */}
            <ConditionalRender if={props.setFilterOptions}>
                {props.setFilterOptions &&
                    <FilterBar setFilterOptions={props.setFilterOptions} />}
            </ConditionalRender>

            <Routes>
                <Route
                    path='/text'
                    element={
                        <TextAndPlan
                            setExecutionPlanOptions={setExecutionPlanOptions}
                            batchId={batchId}
                            batchStatementId={batchStatementId}
                            instance={instance}
                            statementId={statementId}
                            statementType={statementType}
                            layout={layout}
                        />
                    }
                />

                {/* Statement Activity routes */}
                <Route
                    path='/activity'
                    element={
                        <StatementActivity
                            eventTypes={props.eventTypes}
                            instance={instance}
                            batchId={batchId}
                            batchStatementId={batchStatementId}

                            chartPlanPlotLines={chartPlanPlotLines}
                            statementId={statementId}
                            setFilterOptions={props.setFilterOptions}
                            statementType={statementType}
                        />
                    }
                />

                {/* SQL Statistics routes */}
                <Route
                    path='/sql-statistics'
                    element={
                        <SqlStatistics
                            instance={instance}
                            filterParameters={filterParameters}

                            batchStatementId={batchStatementId}
                            statementId={statementId || "0"}
                            statementType={statementType}
                            onStatementClick={openPanel}
                        />
                    }
                />

                <Route
                    path='/events'
                    element={
                        <Events
                            instance={instance}

                            batchStatementId={batchStatementId}
                            statementId={statementId || "0"}
                        />
                    }
                />
            </Routes>

            <ConditionalRender if={statementType.type === 'groupedstatement'}>
                <Routes>
                    <Route path="/batches"
                        element={<GroupedBatches filtersParameters={filterParameters}
                            instanceId={instance.id} statementId={statementId}
                            showByDefault={true}
                            isBatchesForStatement={true}
                            onStatementClick={openPanel}
                        />} />
                </Routes>
            </ConditionalRender>

            <ConditionalRender if={statementType.type !== 'groupedstatement'}>
                <Routes>
                    <Route path="/batches"
                        element={<Batches filtersParameters={filterParameters}
                            instanceId={instance.id}
                            statementId={statementId}
                            batchId={batchId}
                            showByDefault={true}
                            isBatchesForStatement={true}
                            onStatementClick={openPanel}
                        />} />
                </Routes>
            </ConditionalRender>

            {/* Statement Details Slide-out Panel */}
            {selectedStatement && (
                <StatementDetailsPanel
                    key={selectedStatement}
                    statementId={selectedStatement}
                    open={panelOpen}
                    instance={instance}
                    onClose={closePanel}
                    statementType={selectedStatementType}
                    onStatementClick={openPanel}
                />
            )}
        </div>
    )
}

Statement.propTypes = {
    toggleMenu: PropTypes.func.isRequired,
    setFilterOptions: PropTypes.func.isRequired
};

export default Statement;